import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { Add, Delete, Edit, Inventory2 } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateBagTypeMutation,
  useDeleteBagTypeMutation,
  useGetBagTypesQuery,
  useUpdateBagTypeMutation,
} from "../../apis/appApi"

type BagTypeFormData = {
  name: string
  shortName: string
}

const emptyForm: BagTypeFormData = {
  name: "",
  shortName: "",
}

export const BagTypesTab: FC = () => {
  const { t } = useTranslation()
  const { data: bagTypes, isLoading } = useGetBagTypesQuery()
  const [createBagType] = useCreateBagTypeMutation()
  const [updateBagType] = useUpdateBagTypeMutation()
  const [deleteBagType] = useDeleteBagTypeMutation()

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<BagTypeFormData>(emptyForm)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: "success" | "error"
    message: string
  }>({ open: false, severity: "success", message: "" })

  const handleCreate = useCallback(() => {
    setEditingId(null)
    setForm(emptyForm)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((bagType: any) => {
    setEditingId(bagType.id)
    setForm({
      name: bagType.name ?? "",
      shortName: bagType.shortName ?? "",
    })
    setFormOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormOpen(false)
    setEditingId(null)
    setForm(emptyForm)
  }, [])

  const handleFormSubmit = async () => {
    try {
      if (editingId) {
        await updateBagType({
          id: editingId,
          body: { name: form.name, shortName: form.shortName },
        })
      } else {
        await createBagType({
          name: form.name,
          shortName: form.shortName,
        })
      }
      handleFormClose()
      setSnackbar({
        open: true,
        severity: "success",
        message: t("configuration.bagTypes.saved"),
      })
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: t("configuration.bagTypes.saved"),
      })
    }
  }

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      try {
        await deleteBagType(deleteConfirmId)
        setDeleteConfirmId(null)
        setSnackbar({
          open: true,
          severity: "success",
          message: t("configuration.bagTypes.deleted"),
        })
      } catch {
        setSnackbar({
          open: true,
          severity: "error",
          message: t("configuration.bagTypes.deleted"),
        })
      }
    }
  }

  const rows = bagTypes ?? []
  const hasNoData = rows.length === 0

  return (
    <Box {...uiSurface("UI-DLV2-185")} data-testid="bag-types-tab" sx={{ p: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t("configuration.bagTypes.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("configuration.bagTypes.subtitle")}
          </Typography>
        </Box>
        <Button
          data-testid="bag-types-create-btn"
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
          sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
        >
          {t("configuration.bagTypes.create")}
        </Button>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <Box
          data-testid="bag-types-loading"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress />
        </Box>
      ) : hasNoData ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Stack
            data-testid="bag-types-empty-state"
            alignItems="center"
            spacing={2}
            sx={{ maxWidth: 400, textAlign: "center" }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "primary.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <Inventory2 sx={{ fontSize: 40, color: "primary.main" }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              {t("configuration.bagTypes.empty")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {t("configuration.bagTypes.emptyDescription")}
            </Typography>
            <Button
              data-testid="bag-types-empty-create-btn"
              variant="outlined"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("configuration.bagTypes.create")}
            </Button>
          </Stack>
        </Box>
      ) : (
        <TableContainer
          data-testid="bag-types-table"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            "& .MuiTableCell-head": {
              bgcolor: "grey.50",
              fontWeight: 600,
              fontSize: 12,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              py: 1.5,
            },
            "& .MuiTableCell-body": {
              py: 1.5,
            },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("configuration.bagTypes.name")}</TableCell>
                <TableCell>{t("configuration.bagTypes.shortName")}</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  {t("configuration.bagTypes.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((bagType: any) => (
                <TableRow
                  key={bagType.id}
                  data-testid={`bag-types-row-${bagType.id}`}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {bagType.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {bagType.shortName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <Tooltip title={t("configuration.bagTypes.edit")}>
                        <IconButton
                          data-testid={`bag-types-edit-${bagType.id}-btn`}
                          size="small"
                          onClick={() => handleEdit(bagType)}
                          sx={{
                            color: "primary.main",
                            "&:hover": { bgcolor: "primary.50" },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("configuration.bagTypes.deleteTitle")}>
                        <IconButton
                          data-testid={`bag-types-delete-${bagType.id}-btn`}
                          size="small"
                          onClick={() => setDeleteConfirmId(bagType.id)}
                          sx={{
                            color: "error.main",
                            "&:hover": { bgcolor: "error.50" },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        data-testid="bag-type-form-dialog"
        open={formOpen}
        onClose={handleFormClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ ...uiPaper("UI-DLV2-186"), sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          {editingId
            ? t("configuration.bagTypes.edit")
            : t("configuration.bagTypes.create")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1, pt: 1 }}>
            <TextField
              data-testid="bag-type-name-input"
              label={t("configuration.bagTypes.name")}
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              variant="outlined"
              required
            />
            <TextField
              data-testid="bag-type-shortName-input"
              label={t("configuration.bagTypes.shortName")}
              fullWidth
              value={form.shortName}
              onChange={(e) => setForm({ ...form, shortName: e.target.value })}
              variant="outlined"
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-testid="bag-type-form-cancel-btn"
            onClick={handleFormClose}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="bag-type-form-submit-btn"
            variant="contained"
            onClick={handleFormSubmit}
            disabled={!form.name.trim() || !form.shortName.trim()}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        data-testid="bag-type-delete-dialog"
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        PaperProps={{ ...uiPaper("UI-DLV2-187"), sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("configuration.bagTypes.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("configuration.bagTypes.deleteConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-testid="bag-type-delete-cancel-btn"
            onClick={() => setDeleteConfirmId(null)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="bag-type-delete-confirm-btn"
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
